// @flow
import * as React from 'react';
import {useEffect, useState, useMemo} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';

import {loadStripe} from '@stripe/stripe-js';
import {
  Elements,
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';

import {useAccount} from 'docs/data';
import {Callout} from 'docs/components';

import Box from 'sail/Box';

const messages = defineMessages({
  callout: {
    id: 'docsApp.components.aymentRequestButtonElementDemo.callout',
    description: 'Label for a callout',
    defaultMessage:
      '<b>Either your browser does not support the Payment Request API, or you do not have a saved payment method</b>. To try out the Payment Request Button live demo, switch to one of the <a>supported browsers</a> below, and make sure you have a saved payment method.',
  },
});

const useOptions = (paymentRequest) => {
  const options = useMemo(
    () => ({
      paymentRequest,
      style: {
        paymentRequestButton: {
          height: '50px',
        },
      },
    }),
    [paymentRequest],
  );
  return options;
};

const usePaymentRequest = ({options, onPaymentMethod}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(null);

  useEffect(() => {
    if (stripe && paymentRequest === null) {
      const pr = stripe.paymentRequest({
        ...options,
      });
      setPaymentRequest(pr);
    }
  }, [stripe, options, paymentRequest]);

  useEffect(() => {
    let subscribed = true;
    if (paymentRequest) {
      paymentRequest.canMakePayment().then((res) => {
        if (res && subscribed) {
          setCanMakePayment(true);
        }
      });
    }

    return () => {
      subscribed = false;
    };
  }, [paymentRequest]);

  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on('paymentmethod', onPaymentMethod);
    }
    return () => {
      if (paymentRequest) {
        paymentRequest.off('paymentmethod', onPaymentMethod);
      }
    };
  }, [paymentRequest, onPaymentMethod]);

  return canMakePayment ? paymentRequest : null;
};

const Demo = () => {
  const paymentRequest = usePaymentRequest({
    options: {
      country: 'US',
      currency: 'usd',
      total: {
        label: "Stripe (won't be charged)",
        amount: 1099,
      },
    },
    onPaymentMethod: ({complete}) => {
      complete('success');
    },
  });

  const options = useOptions(paymentRequest);

  if (!paymentRequest) {
    return (
      <Callout type="caution">
        <FormattedMessage
          {...messages.callout}
          values={{
            a: (label) => <a href="#html-js-testing">{label}</a>,
            b: (label) => <b>{label}</b>,
          }}
        />
      </Callout>
    );
  }

  return (
    <Box style={{width: '300px', paddingBottom: '48px', paddingTop: '24px'}}>
      <PaymentRequestButtonElement options={options} />
    </Box>
  );
};

export const PaymentRequestButtonElementDemo = () => {
  const {account} = useAccount();
  const [promise, setPromise] = useState(null);

  useEffect(() => {
    setPromise(loadStripe(account.keys.publishable));
  }, [account.keys.publishable]);

  return (
    <Elements stripe={promise}>
      <Demo />
    </Elements>
  );
};
